/**
 * PostMessage Bridge
 * Handles bidirectional communication between the widget iframe and parent window
 */

class PostMessageBridge {
  /**
   * Create a new PostMessage bridge
   * @param {string} targetOrigin - Allowed target origin for postMessage (default: '*')
   */
  constructor(targetOrigin = '*') {
    this.targetOrigin = targetOrigin;
    this.listeners = new Map();
    this.isInitialized = false;
    this.SOURCE_WIDGET = 'kuberpay-widget';
    this.SOURCE_PARENT = 'kuberpay-parent';
    this.handleMessage = this.handleMessage.bind(this);
  }

  /**
   * Send a message to the parent window
   * @param {string} type - Message type
   * @param {Object} payload - Message payload
   */
  send(type, payload) {
    if (window.parent !== window) {
      // If targetOrigin has multiple comma-separated origins, browser postMessage requires a single origin or '*'
      const postTarget =
        this.targetOrigin && !this.targetOrigin.includes(',')
          ? this.targetOrigin.replace(/\/+$/, '')
          : '*';

      window.parent.postMessage(
        {
          type,
          payload,
          source: this.SOURCE_WIDGET,
          timestamp: Date.now(),
        },
        postTarget
      );
    }
  }

  /**
   * Register a listener for a specific message type from parent
   * @param {string} type - Message type to listen for
   * @param {Function} callback - Callback function to execute
   */
  on(type, callback) {
    this.listeners.set(type, callback);
  }

  /**
   * Remove a listener for a specific message type
   * @param {string} type - Message type to stop listening for
   */
  off(type) {
    this.listeners.delete(type);
  }

  /**
   * Message event handler
   */
  handleMessage(event) {
    // 1. Only process messages intended for this bridge from parent
    // Ignore internal window events, third-party SDKs, extensions, etc.
    if (event.data?.source !== this.SOURCE_PARENT) return;

    // 2. Validate origin if specified (and not wildcard)
    if (this.targetOrigin && this.targetOrigin !== '*') {
      const allowedOrigins = this.targetOrigin
        .split(',')
        .map((o) => o.trim().replace(/\/+$/, '').toLowerCase());
      const cleanOrigin = (event.origin || '').trim().replace(/\/+$/, '').toLowerCase();

      if (cleanOrigin && !allowedOrigins.includes(cleanOrigin) && !allowedOrigins.includes('*')) {
        console.warn('PostMessageBridge: Rejected message from unauthorized origin:', event.origin);
        return;
      }
    }

    const { type, payload } = event.data;
    const handler = this.listeners.get(type);

    if (handler) {
      try {
        handler(payload, event);
      } catch (error) {
        console.error('PostMessageBridge: Error in handler for', type, error);
      }
    }
  }

  /**
   * Initialize the bridge and start listening for messages
   */
  init() {
    if (this.isInitialized) return;

    window.addEventListener('message', this.handleMessage);
    this.isInitialized = true;

    // Notify parent that widget is ready
    this.onReady();
  }

  /**
   * Destroy the bridge and clean up listeners
   */
  destroy() {
    if (this.isInitialized) {
      window.removeEventListener('message', this.handleMessage);
    }
    this.listeners.clear();
    this.isInitialized = false;
  }

  // ========================================
  // Predefined Widget -> Parent Events
  // ========================================

  /**
   * Notify parent that widget is ready
   */
  onReady() {
    this.send('WIDGET_READY', { timestamp: Date.now() });
  }

  /**
   * Notify parent when a quote is selected
   * @param {Object} quote - The selected quote object
   */
  onQuoteSelected(quote) {
    this.send('QUOTE_SELECTED', {
      provider: quote.provider,
      cryptoAmount: quote.cryptoAmount,
      fiatAmount: quote.fiatAmount,
      exchangeRate: quote.conversionPrice,
      fees: quote.fees,
      isBestPrice: quote.isBestPrice,
      paymentMethod: quote.paymentMethod || quote.paymentOption || (quote.availablePaymentMethods && quote.availablePaymentMethods[0]?.id) || undefined,
    });
  }

  /**
   * Notify parent when provider widget is opened
   * @param {string} provider - The provider name
   */
  onProviderOpened(provider) {
    this.send('PROVIDER_OPENED', { provider, timestamp: Date.now() });
  }

  /**
   * Notify parent when a purchase is initiated (purchaseId available for correlation)
   * @param {Object} data - Purchase initiation details
   */
  onPurchaseInitiated(data) {
    this.send('PURCHASE_INITIATED', {
      purchaseId: data.purchaseId,
      provider: data.provider,
      walletAddress: data.walletAddress,
      fiatAmount: data.fiatAmount,
      cryptoAmount: data.cryptoAmount,
      fiatCurrency: data.fiatCurrency,
      cryptoCurrency: data.cryptoCurrency,
      network: data.network,
      paymentMethod: data.paymentMethod,
      widgetUrl: data.widgetUrl,
      type: data.type,
      isBuyOrSell: data.isBuyOrSell,
    });
  }

  /**
   * Notify parent when a transaction is complete
   * @param {Object} transaction - Transaction details
   */
  onTransactionComplete(transaction) {
    this.send('TRANSACTION_COMPLETE', transaction);
  }

  /**
   * Notify parent of an error
   * @param {Object} error - Error object with type and message
   */
  onError(error) {
    this.send('ERROR', {
      type: error.type || 'UNKNOWN_ERROR',
      message: error.message || 'An unknown error occurred',
      timestamp: Date.now(),
    });
  }

  /**
   * Notify parent when configuration changes
   * @param {Object} config - Updated configuration
   */
  onConfigChange(config) {
    this.send('CONFIG_CHANGED', config);
  }

  /**
   * Request parent to close the widget
   */
  requestClose() {
    this.send('CLOSE_REQUESTED', { timestamp: Date.now() });
  }

  /**
   * Request parent to resize the iframe
   * @param {number} width - Desired width
   * @param {number} height - Desired height
   */
  requestResize(width, height) {
    this.send('RESIZE_REQUESTED', { width, height });
  }
}

export default PostMessageBridge;
